import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import * as Location from 'expo-location';
import Svg, { Circle, Line, Polygon, Polyline } from 'react-native-svg';
import { PADIPRO, RADIUS, SPACE, padiproShadow, padiproShadowMd } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

const R = 6371008.8;
const RANTE_M2 = 400;
const MAX_ACCURACY = 12;
const SIDE_CALIBRATION_MS = 3000;
const SIDE_MIN_MOVE = 5;
const rad = (v) => Number(v) * Math.PI / 180;
const dist = (a, b) => {
  if (!a || !b) return 0;
  const p1 = rad(a.latitude);
  const p2 = rad(b.latitude);
  const dp = p2 - p1;
  const dl = rad(b.longitude - a.longitude);
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};
const xy = (points) => {
  if (!points.length) return [];
  const o = points[0];
  const p = rad(o.latitude);
  return points.map((x) => ({
    x: R * rad(x.longitude - o.longitude) * Math.cos(p),
    y: R * rad(x.latitude - o.latitude),
  }));
};
const polygonArea = (points) => {
  if (points.length < 3) return 0;
  const local = xy(points);
  let sum = 0;
  local.forEach((p, i) => {
    const n = local[(i + 1) % local.length];
    sum += p.x * n.y - n.x * p.y;
  });
  return Math.abs(sum) / 2;
};
const simplifyPath = (points, tolerance = 1.5) => {
  if (points.length <= 2) return points;
  const local = xy(points);
  const keep = new Set([0, points.length - 1]);
  const visit = (start, end) => {
    const a = local[start];
    const b = local[end];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSquared = dx * dx + dy * dy;
    let farthest = 0;
    let index = -1;
    for (let i = start + 1; i < end; i += 1) {
      const p = local[i];
      const ratio = lengthSquared ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared)) : 0;
      const px = a.x + ratio * dx;
      const py = a.y + ratio * dy;
      const distance = Math.hypot(p.x - px, p.y - py);
      if (distance > farthest) {
        farthest = distance;
        index = i;
      }
    }
    if (index >= 0 && farthest > tolerance) {
      keep.add(index);
      visit(start, index);
      visit(index, end);
    }
  };
  visit(0, points.length - 1);
  return points.filter((_, index) => keep.has(index));
};
const walked = (points) => points.slice(1).reduce((sum, p, i) => sum + dist(points[i], p), 0);
const perimeter = (points) => points.length < 2 ? 0 : walked(points) + (points.length > 2 ? dist(points.at(-1), points[0]) : 0);
const weightedPoint = (samples) => {
  if (!samples.length) return null;
  let total = 0;
  let latitude = 0;
  let longitude = 0;
  samples.forEach((point) => {
    const accuracy = Math.max(1, Number(point.accuracy) || MAX_ACCURACY);
    const weight = 1 / (accuracy * accuracy);
    total += weight;
    latitude += point.latitude * weight;
    longitude += point.longitude * weight;
  });
  return {
    latitude: latitude / total,
    longitude: longitude / total,
    accuracy: Math.min(...samples.map((point) => Number(point.accuracy) || MAX_ACCURACY)),
    speed: (() => {
      const speeds = samples.map((point) => Number(point.speed)).filter((value) => Number.isFinite(value) && value >= 0);
      return speeds.length ? speeds.reduce((sum, value) => sum + value, 0) / speeds.length : null;
    })(),
    timestamp: Math.max(...samples.map((point) => Number(point.timestamp) || 0)),
  };
};
const fmt = (value, digits = 1) => Number(value || 0).toLocaleString('id-ID', { maximumFractionDigits: digits });
const duration = (value) => String(Math.floor(value / 60)).padStart(2, '0') + ':' + String(value % 60).padStart(2, '0');
const quality = (accuracy) => {
  const n = Number(accuracy);
  if (!Number.isFinite(n)) return ['Menunggu GPS', PADIPRO.muted, PADIPRO.surfaceSunken];
  if (n <= 5) return ['Sangat baik', PADIPRO.primary, PADIPRO.primarySoft];
  if (n <= 8) return ['Baik', PADIPRO.sky, PADIPRO.skySoft];
  if (n <= MAX_ACCURACY) return ['Cukup', PADIPRO.warning, PADIPRO.warningSoft];
  return ['Belum akurat', PADIPRO.danger, PADIPRO.dangerSoft];
};
const plot = (points) => {
  if (!points.length) return [];
  const local = xy(points);
  const xs = local.map((p) => p.x);
  const ys = local.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const rx = Math.max(1, maxX - minX);
  const ry = Math.max(1, maxY - minY);
  const scale = Math.min(276 / rx, 166 / ry);
  const ox = (320 - rx * scale) / 2;
  const oy = (210 - ry * scale) / 2;
  return local.map((p) => ({ x: ox + (p.x - minX) * scale, y: 210 - (oy + (p.y - minY) * scale) }));
};

function Metric({ label, value, unit, main }) {
  return (
    <View style={[s.metric, main && s.metricMain]}>
      <Text style={[s.metricLabel, main && s.gold]}>{label}</Text>
      <View style={s.valueRow}>
        <Text style={[s.metricValue, main && s.gold]} numberOfLines={1} adjustsFontSizeToFit>{value}</Text>
        <Text style={[s.metricUnit, main && s.gold]}>{unit}</Text>
      </View>
    </View>
  );
}

function Trace({ points, finished, tracking }) {
  const displayPoints = useMemo(() => simplifyPath(points), [points]);
  const p = useMemo(() => plot(displayPoints), [displayPoints]);
  const text = p.map((x) => x.x + ',' + x.y).join(' ');
  const scale = useMemo(() => {
    if (displayPoints.length < 2) return null;
    const local = xy(displayPoints);
    const xs = local.map((point) => point.x);
    const ys = local.map((point) => point.y);
    const span = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), 1);
    const options = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000];
    const meters = options.find((value) => value >= span / 4) || Math.ceil(span / 400) * 100;
    return { meters, width: Math.max(34, Math.min(96, (meters / span) * 220)) };
  }, [displayPoints]);
  return (
    <View style={s.card}>
      <View style={s.head}>
        <View><Text style={s.cardTitle}>Denah Lahan</Text><Text style={s.muted}>{finished ? 'Bentuk akhir dari jalur GPS' : tracking ? points.length + ' titik • denah mengikuti perjalanan' : 'Mulai dari salah satu sudut'}</Text></View>
        <View style={[s.live, finished && s.doneBadge]}><View style={[s.dot, finished && s.doneDot]} /><Text style={[s.liveText, finished && s.doneText]}>{finished ? 'SELESAI' : tracking ? 'LIVE' : 'SIAP'}</Text></View>
      </View>
      <View style={s.map}>
        <Svg width="100%" height="100%" viewBox="0 0 320 210">
          {[1, 2, 3].map((n) => <Line key={'v' + n} x1={80 * n} y1="0" x2={80 * n} y2="210" stroke="#E8E3D8" />)}
          {[1, 2, 3].map((n) => <Line key={'h' + n} x1="0" y1={52.5 * n} x2="320" y2={52.5 * n} stroke="#E8E3D8" />)}
          {finished && p.length > 2
            ? <Polygon points={text} fill="#DCEBDD" stroke={PADIPRO.primary} strokeWidth="3" />
            : p.length > 1 ? <Polyline points={text} fill="none" stroke={PADIPRO.primary} strokeWidth="3" /> : null}
          {tracking && p.length > 2 ? <Line x1={p.at(-1).x} y1={p.at(-1).y} x2={p[0].x} y2={p[0].y} stroke={PADIPRO.goldDeep} strokeWidth="1.5" strokeDasharray="6 5" /> : null}
          {p[0] ? <Circle cx={p[0].x} cy={p[0].y} r="6" fill={PADIPRO.gold} stroke="#fff" strokeWidth="2" /> : null}
          {p.length > 1 ? <Circle cx={p.at(-1).x} cy={p.at(-1).y} r="5" fill={PADIPRO.primary} stroke="#fff" strokeWidth="2" /> : null}
        </Svg>
        <View style={s.compass}><Text style={s.compassText}>N</Text><View style={s.compassNeedle} /></View>
        {scale ? <View style={s.scaleBox}><View style={[s.scaleLine, { width: scale.width }]} /><Text style={s.scaleText}>{fmt(scale.meters, 0)} m</Text></View> : null}
        {!points.length ? <View style={s.emptyMap}><Text style={s.plus}>+</Text><Text style={s.muted}>Jejak GPS tampil di sini</Text></View> : null}
      </View>
      {points.length ? <View style={s.mapLegend}>
        <View style={s.legendItem}><View style={[s.legendDot, { backgroundColor: PADIPRO.gold }]} /><Text style={s.legendText}>Titik mulai</Text></View>
        <View style={s.legendItem}><View style={[s.legendDot, { backgroundColor: PADIPRO.primary }]} /><Text style={s.legendText}>Posisi terakhir</Text></View>
        {tracking ? <Text style={s.legendHint}>Garis putus-putus = arah kembali</Text> : null}
      </View> : null}
    </View>
  );
}

export default function LandMeasureScreen() {
  const { contentWidthStyle } = useResponsive();
  const [mode, setMode] = useState('walk');
  const [points, setPoints] = useState([]);
  const [latest, setLatest] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [paused, setPaused] = useState(false);
  const [starting, setStarting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [rejected, setRejected] = useState(0);
  const [sideStage, setSideStage] = useState('idle');
  const [sideLength, setSideLength] = useState(0);
  const [sideWidth, setSideWidth] = useState(0);
  const [calibrating, setCalibrating] = useState(false);
  const [sideLockCount, setSideLockCount] = useState(0);
  const [walkCalibrating, setWalkCalibrating] = useState(false);
  const watch = useRef(null);
  const pointsRef = useRef([]);
  const latestRef = useRef(null);
  const trackingRef = useRef(false);
  const pausedRef = useRef(false);
  const sideStageRef = useRef(null);
  const sideStartRef = useRef(null);
  const sideSamplesRef = useRef([]);
  const sideAnchorSamplesRef = useRef([]);
  const sideMovedRef = useRef(false);
  const sideMoveStreakRef = useRef(0);
  const sideCalibratingRef = useRef(false);
  const calibrationTimerRef = useRef(null);
  const walkRawRef = useRef([]);
  const walkAnchorSamplesRef = useRef([]);
  const walkCalibratingRef = useRef(false);
  const walkCalibrationTimerRef = useRef(null);

  useEffect(() => { pointsRef.current = points; }, [points]);
  useEffect(() => { trackingRef.current = tracking; }, [tracking]);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  const stop = useCallback(() => {
    if (walkCalibrationTimerRef.current) clearTimeout(walkCalibrationTimerRef.current);
    walkCalibrationTimerRef.current = null;
    walkCalibratingRef.current = false;
    setWalkCalibrating(false);
    if (calibrationTimerRef.current) clearTimeout(calibrationTimerRef.current);
    calibrationTimerRef.current = null;
    sideCalibratingRef.current = false;
    setCalibrating(false);
    watch.current?.remove();
    watch.current = null;
    trackingRef.current = false;
    pausedRef.current = false;
    setTracking(false);
    setPaused(false);
  }, []);
  useEffect(() => () => stop(), [stop]);
  useEffect(() => {
    if (!tracking || paused) return undefined;
    const id = setInterval(() => setSeconds((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, [tracking, paused]);

  const receive = useCallback((location, force = false) => {
    if (!location?.coords) return;
    const p = {
      latitude: Number(location.coords.latitude),
      longitude: Number(location.coords.longitude),
      accuracy: Number(location.coords.accuracy),
      speed: location.coords.speed == null ? null : Number(location.coords.speed),
      timestamp: Number(location.timestamp || Date.now()),
    };
    latestRef.current = p;
    setLatest(p);
    if (!trackingRef.current || pausedRef.current) return;
    if (sideStageRef.current && sideStartRef.current) {
      if (sideCalibratingRef.current) {
        if (Number.isFinite(p.accuracy) && p.accuracy <= MAX_ACCURACY) {
          sideAnchorSamplesRef.current = [...sideAnchorSamplesRef.current, p].slice(-12);
          const count = Math.min(3, sideAnchorSamplesRef.current.length);
          setSideLockCount(count);
          sideStartRef.current = weightedPoint(sideAnchorSamplesRef.current) || sideStartRef.current;
          if (sideAnchorSamplesRef.current.length >= 3) {
            sideSamplesRef.current = [];
            sideCalibratingRef.current = false;
            setCalibrating(false);
          }
        } else {
          setRejected((v) => v + 1);
        }
        if (sideStageRef.current === 'length') setSideLength(0);
        if (sideStageRef.current === 'width') setSideWidth(0);
        return;
      }
      if (!force && (!Number.isFinite(p.accuracy) || p.accuracy > MAX_ACCURACY)) {
        setRejected((v) => v + 1);
        return;
      }
      sideSamplesRef.current = [...sideSamplesRef.current, p].slice(-5);
      const smoothPoint = weightedPoint(sideSamplesRef.current) || p;
      const rawMeters = dist(sideStartRef.current, smoothPoint);
      const noiseRadius = Math.max(
        SIDE_MIN_MOVE,
        Math.min(MAX_ACCURACY, Math.max(sideStartRef.current.accuracy || 0, smoothPoint.accuracy || 0))
      );
      if (!sideMovedRef.current) {
        const walkingBySpeed = Number.isFinite(smoothPoint.speed) && smoothPoint.speed >= 0.35;
        if (rawMeters <= noiseRadius && !walkingBySpeed) {
          sideMoveStreakRef.current = 0;
          if (sideStageRef.current === 'length') setSideLength(0);
          if (sideStageRef.current === 'width') setSideWidth(0);
          return;
        }
        sideMoveStreakRef.current += 1;
        if (!walkingBySpeed && sideMoveStreakRef.current < 2) return;
        sideMovedRef.current = true;
      }
      if (sideStageRef.current === 'length') setSideLength(rawMeters);
      if (sideStageRef.current === 'width') setSideWidth(rawMeters);
      return;
    }
    if (!force && (!Number.isFinite(p.accuracy) || p.accuracy > MAX_ACCURACY)) {
      setRejected((v) => v + 1);
      return;
    }
    walkRawRef.current = [...walkRawRef.current, p].slice(-4);
    if (walkCalibratingRef.current) {
      walkAnchorSamplesRef.current = [...walkAnchorSamplesRef.current, p].slice(-12);
      const anchor = weightedPoint(walkAnchorSamplesRef.current);
      if (anchor) {
        pointsRef.current = [anchor];
        setPoints([anchor]);
      }
      return;
    }
    const filteredPoint = force ? (weightedPoint(walkRawRef.current) || p) : (weightedPoint(walkRawRef.current) || p);
    const last = pointsRef.current.at(-1);
    if (last) {
      const meters = dist(last, filteredPoint);
      const elapsed = Math.max(1, (filteredPoint.timestamp - last.timestamp) / 1000);
      if (!force && meters > Math.max(30, elapsed * 8 + Math.max(filteredPoint.accuracy || 0, last.accuracy || 0))) {
        setRejected((v) => v + 1);
        return;
      }
      const minStep = Math.max(2, Math.min(5, Number(filteredPoint.accuracy || MAX_ACCURACY) * .5));
      if (!force && meters < minStep) return;
      if (force && meters < .5) return;
    }
    const next = [...pointsRef.current, filteredPoint];
    pointsRef.current = next;
    setPoints(next);
  }, []);

  const reset = useCallback(() => {
    stop();
    pointsRef.current = [];
    latestRef.current = null;
    setPoints([]);
    setLatest(null);
    setFinished(false);
    setSeconds(0);
    setRejected(0);
    walkRawRef.current = [];
    walkAnchorSamplesRef.current = [];
  }, [stop]);

  const start = useCallback(async () => {
    try {
      if (pointsRef.current.length) reset();
      setStarting(true);
      if (!await Location.hasServicesEnabledAsync()) return Alert.alert('GPS belum aktif', 'Aktifkan lokasi/GPS HP lalu coba lagi.');
      const permission = await Location.requestForegroundPermissionsAsync();
      if (!permission.granted) return Alert.alert('Izin lokasi diperlukan', 'Izinkan lokasi presisi untuk menghitung luas.');
      trackingRef.current = true;
      pausedRef.current = false;
      walkRawRef.current = [];
      walkAnchorSamplesRef.current = [];
      walkCalibratingRef.current = true;
      setWalkCalibrating(true);
      setTracking(true);
      setPaused(false);
      setFinished(false);
      try { receive(await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.BestForNavigation })); } catch {}
      watch.current = await Location.watchPositionAsync({
        accuracy: Location.Accuracy.BestForNavigation,
        timeInterval: 1000,
        distanceInterval: 0,
        mayShowUserSettingsDialog: true,
      }, receive);
      walkCalibrationTimerRef.current = setTimeout(() => {
        const anchor = weightedPoint(walkAnchorSamplesRef.current);
        if (anchor) {
          pointsRef.current = [anchor];
          setPoints([anchor]);
        }
        walkRawRef.current = [];
        walkCalibratingRef.current = false;
        walkCalibrationTimerRef.current = null;
        setWalkCalibrating(false);
      }, SIDE_CALIBRATION_MS);
    } catch (error) {
      stop();
      Alert.alert('GPS tidak dapat dibuka', Platform.OS === 'web' ? 'Gunakan HTTPS/localhost dan aktifkan izin lokasi browser.' : error?.message || 'Periksa izin lokasi HP.');
    } finally {
      setStarting(false);
    }
  }, [receive, reset, stop]);

  const startSide = useCallback(async (kind) => {
    try {
      stop();
      setStarting(true);
      if (!await Location.hasServicesEnabledAsync()) {
        Alert.alert('GPS belum aktif', 'Aktifkan lokasi/GPS HP lalu coba lagi.');
        return;
      }
      const permission = await Location.requestForegroundPermissionsAsync();
      if (!permission.granted) {
        Alert.alert('Izin lokasi diperlukan', 'Izinkan lokasi presisi untuk mengukur jarak.');
        return;
      }
      if (Platform.OS === 'android') {
        try { await Location.enableNetworkProviderAsync(); } catch {}
      }
      const first = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.BestForNavigation });
      const startPoint = {
        latitude: Number(first.coords.latitude),
        longitude: Number(first.coords.longitude),
        accuracy: Number(first.coords.accuracy),
        speed: first.coords.speed == null ? null : Number(first.coords.speed),
        timestamp: Number(first.timestamp || Date.now()),
      };
      latestRef.current = startPoint;
      setLatest(startPoint);
      if (kind === 'length') {
        setSideLength(0);
        setSideWidth(0);
      }
      if (kind === 'width') setSideWidth(0);
      sideStartRef.current = startPoint;
      sideSamplesRef.current = [];
      sideAnchorSamplesRef.current = Number.isFinite(startPoint.accuracy) && startPoint.accuracy <= MAX_ACCURACY ? [startPoint] : [];
      setSideLockCount(sideAnchorSamplesRef.current.length);
      sideMovedRef.current = false;
      sideMoveStreakRef.current = 0;
      sideCalibratingRef.current = true;
      setCalibrating(true);
      sideStageRef.current = kind;
      setSideStage(kind);
      setSeconds(0);
      trackingRef.current = true;
      pausedRef.current = false;
      setTracking(true);
      setPaused(false);
      watch.current = await Location.watchPositionAsync({
        accuracy: Location.Accuracy.BestForNavigation,
        timeInterval: 1000,
        distanceInterval: 0,
        mayShowUserSettingsDialog: true,
      }, receive);
    } catch (error) {
      stop();
      sideStageRef.current = null;
      sideStartRef.current = null;
      Alert.alert('GPS tidak dapat dibuka', Platform.OS === 'web' ? 'Gunakan HTTPS/localhost dan aktifkan izin lokasi browser.' : error?.message || 'Periksa izin lokasi HP.');
    } finally {
      setStarting(false);
    }
  }, [receive, stop]);

  const finishSide = useCallback((kind) => {
    if (sideCalibratingRef.current) {
      Alert.alert('Titik awal belum terkunci', 'Tetap diam sampai proses penguncian selesai.');
      return;
    }
    const endPoint = weightedPoint(sideSamplesRef.current) || latestRef.current;
    const meters = sideMovedRef.current && endPoint ? dist(sideStartRef.current, endPoint) : 0;
    if (meters < SIDE_MIN_MOVE) {
      Alert.alert('Belum terdeteksi berjalan', 'Perubahan kecil saat HP diam atau diputar diabaikan. Berjalan lurus lebih dari ' + SIDE_MIN_MOVE + ' meter lalu tekan Stop.');
      return;
    }
    if (kind === 'length') setSideLength(meters);
    if (kind === 'width') setSideWidth(meters);
    stop();
    sideStageRef.current = null;
    sideStartRef.current = null;
    sideSamplesRef.current = [];
    sideAnchorSamplesRef.current = [];
    sideMovedRef.current = false;
    sideMoveStreakRef.current = 0;
    setSideStage(kind === 'length' ? 'length_done' : 'complete');
  }, [stop]);

  const resetSides = useCallback(() => {
    stop();
    sideStageRef.current = null;
    sideStartRef.current = null;
    sideSamplesRef.current = [];
    sideAnchorSamplesRef.current = [];
    sideMovedRef.current = false;
    sideMoveStreakRef.current = 0;
    setSideStage('idle');
    setSideLockCount(0);
    setSideLength(0);
    setSideWidth(0);
    setLatest(null);
    setSeconds(0);
  }, [stop]);

  const markCorner = () => {
    if (!latestRef.current) return Alert.alert('Posisi belum tersedia', 'Tunggu indikator GPS muncul.');
    if (latestRef.current.accuracy > MAX_ACCURACY) return Alert.alert('GPS masih lemah', 'Tunggu akurasi maksimal ' + MAX_ACCURACY + ' meter dahulu.');
    receive({ coords: latestRef.current, timestamp: latestRef.current.timestamp }, true);
  };
  const finishNow = () => { stop(); setFinished(true); };
  const finish = () => {
    if (walkCalibratingRef.current) return Alert.alert('Titik awal belum terkunci', 'Tetap diam sampai proses penguncian selesai.');
    if (latestRef.current?.accuracy <= MAX_ACCURACY) receive({ coords: latestRef.current, timestamp: latestRef.current.timestamp }, true);
    if (pointsRef.current.length < 3) return Alert.alert('Titik belum cukup', 'Minimal tiga titik batas diperlukan.');
    const gap = dist(pointsRef.current[0], pointsRef.current.at(-1));
    if (gap > Math.max(12, Number(latestRef.current?.accuracy || 0) * 2)) {
      return Alert.alert('Belum dekat titik awal', 'Jarak ke titik mulai masih ' + fmt(gap, 1) + ' m.', [
        { text: 'Lanjut berjalan', style: 'cancel' },
        { text: 'Selesaikan saja', onPress: finishNow },
      ]);
    }
    finishNow();
  };

  const area = useMemo(() => polygonArea(points), [points]);
  const fence = useMemo(() => perimeter(points), [points]);
  const path = useMemo(() => walked(points), [points]);
  const gap = points.length > 1 ? dist(points[0], points.at(-1)) : 0;
  const q = quality(latest?.accuracy);
  const sideArea = sideLength * sideWidth;
  const sideFence = sideLength > 0 && sideWidth > 0 ? 2 * (sideLength + sideWidth) : 0;

  return (
    <View style={s.screen}>
      <ScrollView contentContainerStyle={[s.content, contentWidthStyle]} keyboardShouldPersistTaps="handled">
        <View style={s.hero}>
          <Text style={s.eyebrow}>ALAT LAPANGAN</Text>
          <Text style={s.title}>Ukur Luas Tanah</Text>
          <Text style={s.heroSub}>Ukur otomatis dengan GPS: kelilingi batas lahan atau cukup berjalan pada sisi panjang dan lebar.</Text>
          <View style={s.formula}><Text style={s.formulaText}>1 rante = 400 m2</Text></View>
        </View>

        <View style={s.tabs}>
          <TouchableOpacity style={[s.tab, mode === 'walk' && s.tabOn]} onPress={() => { resetSides(); setMode('walk'); }}>
            <Text style={[s.tabTitle, mode === 'walk' && s.tabTitleOn]}>Keliling GPS</Text><Text style={s.tabSub}>Untuk bentuk bebas</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[s.tab, mode === 'sides' && s.tabOn]} onPress={() => { reset(); setMode('sides'); }}>
            <Text style={[s.tabTitle, mode === 'sides' && s.tabTitleOn]}>P × L GPS</Text><Text style={s.tabSub}>Cukup ukur 2 sisi</Text>
          </TouchableOpacity>
        </View>

        {mode === 'walk' ? <>
          <View style={s.card}>
            <View style={s.head}>
              <View><Text style={s.small}>{walkCalibrating ? 'MENGUNCI TITIK AWAL' : 'AKURASI GPS'}</Text><Text style={s.statusValue}>{walkCalibrating ? 'Diam selama 3 detik...' : latest ? '+/- ' + fmt(latest.accuracy, 1) + ' meter' : 'Menunggu posisi'}</Text></View>
              <View style={[s.quality, { backgroundColor: q[2] }]}><Text style={[s.qualityText, { color: q[1] }]}>{q[0]}</Text></View>
            </View>
            <View style={s.divider} />
            <View style={s.stats}>
              <View><Text style={s.stat}>{duration(seconds)}</Text><Text style={s.statLabel}>Durasi</Text></View>
              <View><Text style={s.stat}>{points.length}</Text><Text style={s.statLabel}>Titik</Text></View>
              <View><Text style={s.stat}>{rejected}</Text><Text style={s.statLabel}>Ditolak</Text></View>
              <View><Text style={s.stat}>{fmt(path, 0)} m</Text><Text style={s.statLabel}>Ditempuh</Text></View>
            </View>
          </View>
          <Trace points={points} finished={finished} tracking={tracking && !walkCalibrating} />
          <View style={s.metrics}>
            <Metric label="LUAS TANAH" value={fmt(area, 1)} unit="m2" main />
            <Metric label="HASIL RANTE" value={fmt(area / RANTE_M2, 3)} unit="rante" />
            <Metric label="HEKTARE" value={fmt(area / 10000, 4)} unit="ha" />
            <Metric label="KELILING" value={fmt(fence, 1)} unit="m" />
          </View>
          <View style={s.detail}>
            <View style={s.detailRow}><Text style={s.muted}>Jarak ke titik awal</Text><Text style={[s.detailValue, gap > 15 && { color: PADIPRO.warning }]}>{points.length > 1 ? fmt(gap, 1) + ' m' : '-'}</Text></View>
            <View style={s.detailRow}><Text style={s.muted}>Status</Text><Text style={s.detailValue}>{finished ? 'Selesai' : tracking ? (paused ? 'Dijeda' : 'Merekam') : 'Belum dimulai'}</Text></View>
            <View style={s.detailRow}><Text style={s.muted}>Filter akurasi</Text><Text style={s.detailValue}>Maks. {MAX_ACCURACY} m</Text></View>
          </View>
          {!tracking ? <View style={s.actions}>
            <TouchableOpacity style={s.primary} onPress={start} disabled={starting}>{starting ? <ActivityIndicator color="#fff" /> : <Text style={s.primaryText}>{points.length ? 'Ukur Ulang' : 'Mulai Rekam GPS'}</Text>}</TouchableOpacity>
            {points.length ? <TouchableOpacity style={s.secondary} onPress={reset}><Text style={s.secondaryText}>Hapus Hasil</Text></TouchableOpacity> : null}
          </View> : walkCalibrating ? <View style={s.actions}>
            <View style={[s.primary, s.disabledButton]}><View style={s.lockRow}><ActivityIndicator color="#fff" /><Text style={s.primaryText}>Mengunci Titik Awal</Text></View></View>
            <Text style={s.calibrationHint}>Letakkan HP stabil dan jangan berjalan dahulu.</Text>
          </View> : <View style={s.actions}>
            <View style={s.buttonRow}>
              <TouchableOpacity style={[s.secondary, { flex: 1 }]} onPress={() => { pausedRef.current = !pausedRef.current; setPaused(pausedRef.current); }}><Text style={s.secondaryText}>{paused ? 'Lanjutkan' : 'Jeda'}</Text></TouchableOpacity>
              <TouchableOpacity style={[s.corner, { flex: 1 }]} onPress={markCorner}><Text style={s.cornerText}>Tandai Sudut</Text></TouchableOpacity>
            </View>
            <TouchableOpacity style={s.finish} onPress={finish}><Text style={s.finishText}>Selesai Mengelilingi Lahan</Text></TouchableOpacity>
          </View>}
          <View style={s.guide}>
            <Text style={s.guideTitle}>Agar lebih akurat</Text>
            {['Tekan Mulai dan diam 3 detik sampai titik awal terkunci.', 'Jalan pelan mengikuti batas paling luar.', 'Tekan Tandai Sudut pada setiap belokan penting.', 'Kembali mendekati titik awal lalu tekan Selesai.'].map((text, i) =>
              <View key={text} style={s.guideRow}><View style={s.step}><Text style={s.stepText}>{i + 1}</Text></View><Text style={s.guideText}>{text}</Text></View>)}
            <Text style={s.note}>GPS HP adalah estimasi operasional, bukan pengukuran pertanahan resmi.</Text>
          </View>
        </> : <>
          <View style={s.card}>
            <View style={s.head}>
              <View>
                <Text style={s.small}>PENGUKURAN DUA SISI</Text>
                <Text style={s.statusValue}>{calibrating ? 'Mencari GPS presisi ' + sideLockCount + '/3' : sideStage === 'complete' ? 'Pengukuran selesai' : sideStage === 'length_done' ? 'Lanjut ukur lebar' : sideStage === 'length' ? 'Titik terkunci • ukur panjang' : sideStage === 'width' ? 'Titik terkunci • ukur lebar' : 'Siap mengukur'}</Text>
              </View>
              <View style={s.gpsSummary}>
                <View style={[s.quality, { backgroundColor: q[2] }]}><Text style={[s.qualityText, { color: q[1] }]}>{q[0]}</Text></View>
                <Text style={s.gpsAccuracy}>{latest ? '+/- ' + fmt(latest.accuracy, 1) + ' m' : 'Belum ada sinyal'}</Text>
              </View>
            </View>
            <View style={s.divider} />
            <Text style={s.muted}>{calibrating ? 'Tetap diam. Titik akan terkunci otomatis setelah tiga sinyal akurat diterima.' : 'Setelah titik terkunci, berjalan lurus sampai ujung sisi. Diam sebentar lalu tekan Stop.'}</Text>
          </View>

          <View style={s.sideGrid}>
            <View style={[s.sideCard, sideStage === 'length' && s.sideCardActive]}>
              <View style={s.sideHead}>
                <View style={[s.sideNumber, (sideLength > 0 || sideStage === 'length') && s.sideNumberOn]}><Text style={[s.sideNumberText, (sideLength > 0 || sideStage === 'length') && s.sideNumberTextOn]}>1</Text></View>
                <View style={{ flex: 1 }}><Text style={s.sideLabel}>PANJANG</Text><Text style={s.sideValue}>{fmt(sideLength, 1)} <Text style={s.sideUnit}>meter</Text></Text></View>
              </View>
              {sideStage === 'length'
                ? calibrating ? <View style={s.calibrationActions}><View style={[s.finish, s.disabledButton]}><View style={s.lockRow}><ActivityIndicator color="#fff" /><Text style={s.finishText}>Menunggu GPS {sideLockCount}/3</Text></View></View><TouchableOpacity style={s.cancelLock} onPress={resetSides}><Text style={s.cancelLockText}>Batalkan</Text></TouchableOpacity></View> : <TouchableOpacity style={s.finish} onPress={() => finishSide('length')}><Text style={s.finishText}>Stop Panjang</Text></TouchableOpacity>
                : <TouchableOpacity style={s.primary} onPress={() => startSide('length')} disabled={starting || sideStage === 'width'}>{starting ? <ActivityIndicator color="#fff" /> : <Text style={s.primaryText}>{sideLength > 0 ? 'Ukur Ulang Panjang' : 'Mulai Ukur Panjang'}</Text>}</TouchableOpacity>}
            </View>

            <View style={[s.sideCard, sideStage === 'width' && s.sideCardActive, sideLength <= 0 && s.sideCardDisabled]}>
              <View style={s.sideHead}>
                <View style={[s.sideNumber, (sideWidth > 0 || sideStage === 'width') && s.sideNumberOn]}><Text style={[s.sideNumberText, (sideWidth > 0 || sideStage === 'width') && s.sideNumberTextOn]}>2</Text></View>
                <View style={{ flex: 1 }}><Text style={s.sideLabel}>LEBAR</Text><Text style={s.sideValue}>{fmt(sideWidth, 1)} <Text style={s.sideUnit}>meter</Text></Text></View>
              </View>
              {sideStage === 'width'
                ? calibrating ? <View style={s.calibrationActions}><View style={[s.finish, s.disabledButton]}><View style={s.lockRow}><ActivityIndicator color="#fff" /><Text style={s.finishText}>Menunggu GPS {sideLockCount}/3</Text></View></View><TouchableOpacity style={s.cancelLock} onPress={resetSides}><Text style={s.cancelLockText}>Batalkan</Text></TouchableOpacity></View> : <TouchableOpacity style={s.finish} onPress={() => finishSide('width')}><Text style={s.finishText}>Stop Lebar</Text></TouchableOpacity>
                : <TouchableOpacity style={[s.primary, sideLength <= 0 && s.disabledButton]} onPress={() => startSide('width')} disabled={starting || sideLength <= 0 || sideStage === 'length'}><Text style={s.primaryText}>{sideWidth > 0 ? 'Ukur Ulang Lebar' : 'Mulai Ukur Lebar'}</Text></TouchableOpacity>}
            </View>
          </View>

          <View style={s.equation}><Text style={s.equationText}>{fmt(sideLength, 1)} m × {fmt(sideWidth, 1)} m = {fmt(sideArea, 1)} m2</Text></View>
          <View style={s.metrics}>
            <Metric label="LUAS TANAH" value={fmt(sideArea, 1)} unit="m2" main />
            <Metric label="HASIL RANTE" value={fmt(sideArea / RANTE_M2, 3)} unit="rante" />
            <Metric label="HEKTARE" value={fmt(sideArea / 10000, 4)} unit="ha" />
            <Metric label="KELILING" value={fmt(sideFence, 1)} unit="m" />
          </View>

          <View style={s.guide}>
            <Text style={s.guideTitle}>Cara mengukur</Text>
            {['Tekan Mulai, lalu diam sampai tiga sinyal GPS terkunci.', 'Berjalan lurus; putaran HP saat diam tidak dihitung.', 'Diam sebentar di ujung, lalu tekan Stop.', 'Ulangi cara yang sama untuk sisi lebar.'].map((text, i) =>
              <View key={text} style={s.guideRow}><View style={s.step}><Text style={s.stepText}>{i + 1}</Text></View><Text style={s.guideText}>{text}</Text></View>)}
            <Text style={s.note}>Panjang × Lebar GPS cocok untuk lahan persegi atau persegi panjang. Untuk bentuk tidak beraturan, gunakan Keliling GPS.</Text>
          </View>
          {(sideLength > 0 || sideWidth > 0) ? <TouchableOpacity style={[s.secondary, { marginTop: SPACE.md }]} onPress={resetSides}><Text style={s.secondaryText}>Ulangi Semua Pengukuran</Text></TouchableOpacity> : null}
        </>}
        <View style={{ height: 28 }} />
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  screen: { flex: 1, backgroundColor: PADIPRO.bg },
  content: { width: '100%', alignSelf: 'center', padding: SPACE.lg, paddingBottom: 36 },
  hero: { backgroundColor: PADIPRO.primary, borderRadius: RADIUS.xl, padding: SPACE.xl, marginBottom: SPACE.md, ...padiproShadowMd },
  eyebrow: { color: '#BFD8C7', fontSize: 11, fontWeight: '900', letterSpacing: 1.5 },
  title: { color: '#fff', fontSize: 28, fontWeight: '900', marginTop: 7 },
  heroSub: { color: '#DCE8DF', fontSize: 14, lineHeight: 21, marginTop: 8 },
  formula: { alignSelf: 'flex-start', marginTop: 14, backgroundColor: 'rgba(255,255,255,.13)', borderRadius: RADIUS.pill, paddingHorizontal: 12, paddingVertical: 7 },
  formulaText: { color: '#fff', fontSize: 12, fontWeight: '900' },
  tabs: { flexDirection: 'row', padding: 4, backgroundColor: PADIPRO.surfaceSunken, borderRadius: RADIUS.lg, borderWidth: 1, borderColor: PADIPRO.border, marginBottom: SPACE.md },
  tab: { flex: 1, borderRadius: RADIUS.md, paddingVertical: 11, alignItems: 'center' },
  tabOn: { backgroundColor: PADIPRO.surface, ...padiproShadow },
  tabTitle: { color: PADIPRO.muted, fontSize: 14, fontWeight: '900' },
  tabTitleOn: { color: PADIPRO.primary },
  tabSub: { color: PADIPRO.mutedSoft, fontSize: 10, marginTop: 2 },
  card: { backgroundColor: PADIPRO.surface, borderRadius: RADIUS.lg, borderWidth: 1, borderColor: PADIPRO.border, padding: SPACE.lg, marginBottom: SPACE.md, ...padiproShadow },
  head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  small: { color: PADIPRO.muted, fontSize: 10, fontWeight: '900', letterSpacing: .8 },
  statusValue: { color: PADIPRO.textStrong, fontSize: 17, fontWeight: '900', marginTop: 4 },
  quality: { borderRadius: RADIUS.pill, paddingHorizontal: 11, paddingVertical: 7 },
  qualityText: { fontSize: 10, fontWeight: '900' },
  gpsSummary: { alignItems: 'flex-end', gap: 4 },
  gpsAccuracy: { color: PADIPRO.muted, fontSize: 10, fontWeight: '800' },
  divider: { height: 1, backgroundColor: PADIPRO.border, marginVertical: 14 },
  stats: { flexDirection: 'row', justifyContent: 'space-between' },
  stat: { color: PADIPRO.textStrong, fontSize: 14, fontWeight: '900', textAlign: 'center' },
  statLabel: { color: PADIPRO.muted, fontSize: 10, marginTop: 3, textAlign: 'center' },
  cardTitle: { color: PADIPRO.textStrong, fontSize: 15, fontWeight: '900' },
  muted: { color: PADIPRO.muted, fontSize: 11, marginTop: 3 },
  live: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: PADIPRO.primarySoft, borderRadius: RADIUS.pill, paddingHorizontal: 9, paddingVertical: 5 },
  dot: { width: 6, height: 6, borderRadius: 3, backgroundColor: PADIPRO.primary },
  liveText: { color: PADIPRO.primary, fontSize: 9, fontWeight: '900' },
  doneBadge: { backgroundColor: PADIPRO.goldSoft },
  doneDot: { backgroundColor: PADIPRO.goldDeep },
  doneText: { color: PADIPRO.goldDeep },
  map: { height: 260, marginTop: 10, overflow: 'hidden', borderRadius: RADIUS.md, backgroundColor: '#F8F7F2', borderWidth: 1, borderColor: PADIPRO.border },
  compass: { position: 'absolute', top: 10, right: 10, width: 30, height: 42, borderRadius: 15, backgroundColor: 'rgba(255,255,255,.92)', alignItems: 'center', justifyContent: 'center', ...padiproShadow },
  compassText: { color: PADIPRO.textStrong, fontSize: 10, fontWeight: '900' },
  scaleBox: { position: 'absolute', left: 11, bottom: 10, backgroundColor: 'rgba(255,255,255,.92)', borderRadius: RADIUS.sm, paddingHorizontal: 8, paddingVertical: 6, alignItems: 'flex-start', ...padiproShadow },
  scaleLine: { height: 3, borderLeftWidth: 1, borderRightWidth: 1, borderColor: PADIPRO.textStrong, backgroundColor: PADIPRO.textStrong },
  scaleText: { color: PADIPRO.textStrong, fontSize: 9, fontWeight: '900', marginTop: 3 },
  compassNeedle: { width: 0, height: 0, borderLeftWidth: 4, borderRightWidth: 4, borderBottomWidth: 11, borderLeftColor: 'transparent', borderRightColor: 'transparent', borderBottomColor: PADIPRO.danger, marginTop: 2 },
  mapLegend: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 11 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendText: { color: PADIPRO.muted, fontSize: 10, fontWeight: '700' },
  legendHint: { color: PADIPRO.muted, fontSize: 9, marginLeft: 'auto' },
  emptyMap: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
  plus: { width: 38, height: 38, borderRadius: 19, textAlign: 'center', lineHeight: 36, borderWidth: 1, borderColor: PADIPRO.borderStrong, color: PADIPRO.primary, fontSize: 24, marginBottom: 6 },
  metrics: { flexDirection: 'row', flexWrap: 'wrap', gap: SPACE.sm, marginBottom: SPACE.md },
  metric: { flexGrow: 1, flexBasis: '47%', minWidth: 140, backgroundColor: PADIPRO.surface, borderRadius: RADIUS.lg, borderWidth: 1, borderColor: PADIPRO.border, padding: SPACE.lg },
  metricMain: { backgroundColor: PADIPRO.goldSoft, borderColor: '#E5CF8D' },
  metricLabel: { color: PADIPRO.muted, fontSize: 10, fontWeight: '900', letterSpacing: .7 },
  valueRow: { flexDirection: 'row', alignItems: 'baseline', gap: 5, marginTop: 7 },
  metricValue: { color: PADIPRO.textStrong, fontSize: 23, fontWeight: '900', maxWidth: '78%' },
  metricUnit: { color: PADIPRO.muted, fontSize: 11, fontWeight: '800' },
  gold: { color: PADIPRO.goldDeep },
  detail: { backgroundColor: PADIPRO.surface, borderRadius: RADIUS.lg, borderWidth: 1, borderColor: PADIPRO.border, paddingHorizontal: SPACE.lg, marginBottom: SPACE.md },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 12, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: PADIPRO.border },
  detailValue: { color: PADIPRO.textStrong, fontSize: 12, fontWeight: '900' },
  actions: { gap: SPACE.sm, marginBottom: SPACE.md },
  buttonRow: { flexDirection: 'row', gap: SPACE.sm },
  primary: { minHeight: 52, borderRadius: RADIUS.md, backgroundColor: PADIPRO.primary, alignItems: 'center', justifyContent: 'center', ...padiproShadow },
  primaryText: { color: '#fff', fontSize: 14, fontWeight: '900' },
  secondary: { minHeight: 48, borderRadius: RADIUS.md, backgroundColor: PADIPRO.surface, borderWidth: 1, borderColor: PADIPRO.borderStrong, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 14 },
  secondaryText: { color: PADIPRO.primary, fontSize: 13, fontWeight: '900' },
  corner: { minHeight: 48, borderRadius: RADIUS.md, backgroundColor: PADIPRO.skySoft, borderWidth: 1, borderColor: '#BDD7E1', alignItems: 'center', justifyContent: 'center' },
  cornerText: { color: PADIPRO.sky, fontSize: 13, fontWeight: '900' },
  finish: { minHeight: 52, borderRadius: RADIUS.md, backgroundColor: PADIPRO.goldDeep, alignItems: 'center', justifyContent: 'center', ...padiproShadow },
  finishText: { color: '#fff', fontSize: 14, fontWeight: '900' },
  guide: { backgroundColor: PADIPRO.primarySoft, borderRadius: RADIUS.lg, borderWidth: 1, borderColor: PADIPRO.primarySoftStrong, padding: SPACE.lg },
  guideTitle: { color: PADIPRO.primary, fontSize: 15, fontWeight: '900', marginBottom: 12 },
  guideRow: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  step: { width: 22, height: 22, borderRadius: 11, backgroundColor: PADIPRO.primary, alignItems: 'center', justifyContent: 'center' },
  stepText: { color: '#fff', fontSize: 10, fontWeight: '900' },
  guideText: { flex: 1, color: PADIPRO.text, fontSize: 12, lineHeight: 18 },
  note: { color: PADIPRO.muted, fontSize: 10, lineHeight: 15, borderTopWidth: 1, borderTopColor: PADIPRO.primarySoftStrong, paddingTop: 10 },
  sideGrid: { gap: SPACE.md, marginBottom: SPACE.md },
  sideCard: { backgroundColor: PADIPRO.surface, borderRadius: RADIUS.lg, borderWidth: 1, borderColor: PADIPRO.border, padding: SPACE.lg, gap: SPACE.md, ...padiproShadow },
  sideCardActive: { borderColor: PADIPRO.primary, borderWidth: 2, backgroundColor: PADIPRO.primarySoft },
  sideCardDisabled: { opacity: .58 },
  sideHead: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  sideNumber: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: PADIPRO.surfaceSunken, borderWidth: 1, borderColor: PADIPRO.borderStrong },
  sideNumberOn: { backgroundColor: PADIPRO.primary, borderColor: PADIPRO.primary },
  sideNumberText: { color: PADIPRO.muted, fontSize: 13, fontWeight: '900' },
  sideNumberTextOn: { color: '#fff' },
  sideLabel: { color: PADIPRO.muted, fontSize: 10, fontWeight: '900', letterSpacing: .8 },
  sideValue: { color: PADIPRO.textStrong, fontSize: 25, fontWeight: '900', marginTop: 3 },
  sideUnit: { color: PADIPRO.muted, fontSize: 12, fontWeight: '800' },
  disabledButton: { backgroundColor: PADIPRO.mutedSoft },
  lockRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 9 },
  calibrationHint: { color: PADIPRO.muted, fontSize: 11, textAlign: 'center' },
  calibrationActions: { gap: 8 },
  cancelLock: { minHeight: 40, alignItems: 'center', justifyContent: 'center' },
  cancelLockText: { color: PADIPRO.danger, fontSize: 12, fontWeight: '900' },
  equation: { backgroundColor: PADIPRO.skySoft, borderRadius: RADIUS.md, borderWidth: 1, borderColor: '#C7DCE4', padding: 13, alignItems: 'center', marginBottom: SPACE.md },
  equationText: { color: PADIPRO.sky, fontSize: 13, fontWeight: '900' },
});
